import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { photographyPackages } from '@/mocks/services'
import { buildWhatsAppLink } from '@/utils/whatsapp'

interface PackageItem {
  id: string
  icon: string
  popular: boolean
}

interface PackagesSectionProps {
  packages?: PackageItem[]
  namespace?: string
  whatsappEnquire?: boolean
}

export default function PackagesSection({
  packages = photographyPackages,
  namespace = 'packages',
  whatsappEnquire = false,
}: PackagesSectionProps) {
  const { t } = useTranslation()

  return (
    <section id="packages" className="w-full bg-background-100 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-primary-500" />
            <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">
              {t(`${namespace}.label`)}
            </span>
            <span className="h-px w-10 bg-primary-500" />
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
            {t(`${namespace}.title`)}
          </h2>
          <p className="mt-5 text-base text-foreground-600 leading-relaxed">{t(`${namespace}.intro`)}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {packages.map((pkg) => {
            const features = t(`${namespace}.items.${pkg.id}.features`, { returnObjects: true }) as string[]
            const name = t(`${namespace}.items.${pkg.id}.name`)
            return (
              <article
                key={pkg.id}
                className={`relative rounded-lg border p-6 md:p-7 flex flex-col bg-background-50 ${
                  pkg.popular ? 'border-primary-400' : 'border-background-200'
                }`}
              >
                {pkg.popular && (
                  <span className="absolute -top-3 left-6 text-[10px] tracking-[0.18em] uppercase px-3 py-1 rounded-full bg-primary-500 text-background-50 whitespace-nowrap">
                    {t(`${namespace}.popularBadge`)}
                  </span>
                )}
                <span
                  className={`w-12 h-12 rounded-full flex items-center justify-center mb-5 ${
                    pkg.popular ? 'bg-primary-500 text-background-50' : 'bg-primary-100 text-primary-700'
                  }`}
                >
                  <i className={`${pkg.icon} text-2xl`} />
                </span>
                <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">{name}</h3>
                <p className="text-xs uppercase tracking-wider text-foreground-500 mt-1 mb-4">
                  {t(`${namespace}.items.${pkg.id}.subtitle`)}
                </p>
                <ul className="space-y-3 mb-6 flex-1">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-check-line text-primary-600 text-base mt-0.5 shrink-0" />
                      <span className="leading-snug">{feature}</span>
                    </li>
                  ))}
                </ul>
                {whatsappEnquire ? (
                  <a
                    href={buildWhatsAppLink(t('common.enquireMessage', { topic: name }))}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center justify-center gap-2 h-11 rounded-md text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer transition-colors ${
                      pkg.popular
                        ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                        : 'border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-700'
                    }`}
                  >
                    <i className="ri-whatsapp-line text-base" />
                    {t('common.enquire')}
                  </a>
                ) : (
                  <Link
                    to="/contact"
                    className={`inline-flex items-center justify-center gap-2 h-11 rounded-md text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer transition-colors ${
                      pkg.popular
                        ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                        : 'border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-700'
                    }`}
                  >
                    <i className="ri-whatsapp-line text-base" />
                    {t('common.getQuote')}
                  </Link>
                )}
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}